//Components
import DateQuestion from '../QuestionTypes/DateQuestion/DateQuestion';
import LongQuestion from '../QuestionTypes/LongQuestion/LongQuestion';
import MultipleChoiceQuestion from '../QuestionTypes/MultipleChoiceQuestion/MultipleChoiceQuestion';
import ShortQuestion from '../QuestionTypes/ShortQuestion/ShortQuestion';
import RankingQuestion from '../QuestionTypes/RankingQuestion/RankingQuestion';
import SingleChoiceQuestion from '../QuestionTypes/SingleChoiceQuestion/SingleChoiceQuestion';
import TimeQuestion from '../QuestionTypes/TimeQuestion/TimeQuestion';
import GroupReferenceQuestion from '../QuestionTypes/GroupReferenceQuestion/GroupReferenceQuestion';
import IconTooltipButton from './../../../../../../components/button/IconTooltipButton';
import CircularProgress from '@mui/material/CircularProgress';
import TemplateSelector from './TemplateSelector/TemplateSelector';

//Helpers
import generateNumberList from '../../../helpers/rangeListGenerator';

//Font icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

//Mui Components

import {
  Button,
  Box,
  Collapse,
  InputLabel,
  IconButton,
  Paper,
  List,
  ListItemIcon,
  ListItemText,
  Popper,
  Fade,
  MenuItem,
  Typography,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Select
} from '@mui/material';

//Hooks
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { deleteFiles, presigneUrl, uploadFile } from '../../../../../../services/upload/upload.service';

//Redux Actions
import {
  cloneQuestion,
  updateQuestion,
  removeQuestion,
  setActiveQuestion,
  switchQuestionOrder,
  reorderQuestionOptions
} from '../../../../../../store/actions/dataEntryTemplateGeneratorActions';

//Others
import { rankingIcons } from '../../../constants/rankingIcons';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import globalConfig from '../../../../../../config/globalConfig';
import { DragDropContext } from 'react-beautiful-dnd';

//Custom css
import './Question.css';

const { isDevelopment, storage } = globalConfig;
const bucketFolder = 'dataentry/templates/files';
const origin = isDevelopment ? storage : window.location.origin;

const Question = ({
  index: questionIndex,
  options,
  question,
  showDelete,
  onChangeAlert,
  templateType,
  isLastQuestion,
  isEditable = true
}) => {
  const { type, title, content, id, variant, isActive, error, required, image, config } = question;
  const [anchorEl, setAnchorEl] = useState(null);
  const [placement, setPlacement] = useState();
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [imageError, setImageError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { getRootProps, getInputProps, open, acceptedFiles } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    multiple: false
  });
  const dispatch = useDispatch();
  const isSurvey = templateType?.value === 'survey';
  const isGroupReference = type?.value === 'groupreference';
  const isSingleChoice = type?.value === 'singlechoice' && variant?.value !== 'image';
  const tenant = useSelector((state) => state?.tenant?.tenant ?? {});
  // Track previous values
  const prevTypeRef = useRef(type?.value);
  const prevVariantRef = useRef(variant?.value);

  const handleOnchangeTitle = (event) => {
    event.preventDefault();
    const payload = {
      id,
      title: event.target.value
    };
    dispatch(updateQuestion(payload));
    onChangeAlert();
  };

  const handleOnChangeLayout = (event) => {
    const payload = {
      id,
      config: {
        ...config,
        layout: event.target.value
      }
    };
    dispatch(updateQuestion(payload));
    onChangeAlert();
  };

  const getQuestionBody = () => {
    switch (type?.value) {
      case 'longanswer': {
        return <LongQuestion question={question} templateType={templateType} />;
      }

      case 'multiplechoice': {
        return (
          <MultipleChoiceQuestion
            question={question}
            onChangeAlert={onChangeAlert}
            templateType={templateType}
          />
        );
      }

      case 'singlechoice': {
        return (
          <SingleChoiceQuestion
            question={question}
            onChangeAlert={onChangeAlert}
            templateType={templateType}
          />
        );
      }
      case 'date': {
        return <DateQuestion question={question} templateType={templateType} />;
      }

      case 'time': {
        return <TimeQuestion question={question} templateType={templateType} />;
      }

      case 'ranking': {
        return (
          <RankingQuestion question={question} templateType={templateType} onChangeAlert={onChangeAlert} />
        );
      }

      case 'groupreference': {
        return (
          <GroupReferenceQuestion
            question={question}
            templateType={templateType}
            onChangeAlert={onChangeAlert}
          />
        );
      }

      default: {
        return <ShortQuestion question={question} templateType={templateType} />;
      }
    }
  };

  const questionBody = getQuestionBody();

  const handleOnClickAnchor = (event) => {
    setAnchorEl(event.currentTarget);
    setPlacement('bottom-start');
    setIsOpenPopover(true);
    onChangeAlert();
  };

  const handleOnClose = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpenPopover(false);
  };

  const getTypeLabel = (value) => {
    if (options && options.length > 0) {
      return options.find((option) => {
        if (option.value === value) {
          return option.label;
        }
        return '';
      });
    }

    return '';
  };

  const handleClickQuestionType = (event, option, variant = null) => {
    const { _id, variants, value } = option;

    if (question?.type?.value === value && question?.variant?.value === variant?.value) {
      setIsOpenPopover(false);
      return;
    }

    onChangeAlert();

    //This is a parent question type
    if (variants && variants.length && !variant) {
      setSelectedIndex(_id === selectedIndex ? null : _id);
    } else {
      const payload = {
        id,
        type: {
          value
        },
        options: [],
        variant: null,
        error: {},
        config: {}
      };

      if (value === 'date' || value === 'time') {
        payload.config = { ...option.config };
      }

      if (value === 'singlechoice' || value === 'multiplechoice') {
        payload.config = { maxOptions: -1, layout: 'vertical' }; // Add default layout
        payload.options = [
          {
            id: new Date().getTime(),
            value: '',
            option: '',
            image: null,
            order: 1,
            error: {}
          }
        ];

        if (variant?.value === 'range') {
          payload.options = [...generateNumberList(0, 100, 1)];
          payload.config = {
            range: {
              min: 0,
              max: 100,
              skip: 1
            },
            layout: 'vertical' // Add default layout for range
          };
        }

        if (variant?.value === 'boolean') {
          payload.config = { minOptions: 2, maxOptions: 2, layout: 'vertical' }; // Add default layout
          payload.options = [
            {
              id: new Date().getTime() + 1,
              value: true,
              option: 'Yes',
              order: 1,
              error: {}
            },
            {
              id: new Date().getTime() + 2,
              value: false,
              option: 'No',
              order: 2,
              error: {}
            }
          ];
        }
      }

      if (value === 'ranking') {
        payload.config = {
          minOptions: 2,
          maxOptions: -1,
          color: '#FFC107',
          icon: rankingIcons[0]?.value,
          iconInfo: [`${rankingIcons[0]?.type}`, `${rankingIcons[0]?.value}`]
        };
        payload.options = [
          {
            id: new Date().getTime() + 1,
            value: '',
            option: '',
            order: 1,
            error: {}
          },
          {
            id: new Date().getTime() + 2,
            value: '',
            option: '',
            order: 2,
            error: {}
          }
        ];
      }

      if (variant) {
        payload.variant = { ...variant };
      }

      dispatch(updateQuestion(payload));
      setIsOpenPopover(false);
    }
  };

  const handleRemoveQuestion = (event) => {
    event.stopPropagation();
    event.preventDefault();

    dispatch(removeQuestion(id));
    onChangeAlert();
  };

  const handleOnClickQuestion = () => {
    if (!isEditable) {
      return;
    } else {
      dispatch(setActiveQuestion(id));
      onChangeAlert();
    }
  };

  const handleOnchangeRequired = () => {
    dispatch(updateQuestion({ id, required: !required }));

    onChangeAlert();
  };

  const handleTemplateSelection = (template) => {
    if (!template) {
      setSelectedTemplate(null);
      return;
    }

    setSelectedTemplate(template);

    // Map template options to question options format
    const mappedOptions = template.options.map((opt) => ({
      id: opt.id,
      value: opt.value,
      option: opt.option,
      image: opt.image,
      order: opt.order
    }));

    // Update question with template data
    const payload = {
      id,
      options: mappedOptions,
      config: {
        ...config,
        ...template.config
      }
    };

    dispatch(updateQuestion(payload));
    onChangeAlert();
  };

  const handleOnSwitchQuestion = (event, move) => {
    event.stopPropagation();
    event.preventDefault();
    dispatch(switchQuestionOrder({ id, move }));
    onChangeAlert();
  };

  const handleOnCloneQuestion = (event, questionId) => {
    event.stopPropagation();
    event.preventDefault();
    dispatch(cloneQuestion(questionId));
    onChangeAlert();
  };

  const handleOnDeleteImage = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (image) {
      dispatch(updateQuestion({ id, image: null }));
      onChangeAlert();
    }
  };

  const onUpload = async (file = null) => {
    if (image) {
      const resultDelete = await deleteFiles([{ name: image ?? '' }], bucketFolder);
      if (resultDelete.error) {
        return { error: resultDelete.error.message };
      }
    }

    if (file) {
      const files = [
        {
          file: { path: file?.path ?? '' },
          lastModified: file?.lastModified ?? new Date().getTime(),
          name: file?.name ?? '',
          size: file?.size ?? 0,
          type: file?.type || 'image/png'
        }
      ];

      const resultUpload = await presigneUrl(files, bucketFolder);

      if (resultUpload.error || !resultUpload.data) {
        return { error: resultUpload.error.message };
      } else {
        const dataImage =
          resultUpload?.data && resultUpload?.data?.length ? resultUpload?.data[0]?.data : null;

        if (dataImage) {
          await uploadFile(dataImage.presignedUrl, file);

          const imageUrl = `${origin}/tenants/${tenant?.tenantId}/${bucketFolder}/${dataImage.filename}`;

          return { link: imageUrl };
        }
      }
    }
    return;
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    // If dropped outside the list
    if (!destination) {
      return;
    }

    //Destination droppableId is same as source droppableId(only reordering in same question)
    if (source.droppableId !== destination.droppableId) {
      return;
    }

    // If dropped in the same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Create new array with reordered items
    const newOptions = Array.from(question?.options || []);
    const [movedOption] = newOptions.splice(source.index, 1);
    newOptions.splice(destination.index, 0, movedOption);

    // Update order property for each option
    const updatedOptions = newOptions.map((option, index) => ({
      ...option,
      order: index + 1
    }));

    // Dispatch reorder action
    dispatch(
      reorderQuestionOptions({
        questionId: question?.id,
        options: updatedOptions
      })
    );
    onChangeAlert();
  };

  //Get image from file input
  useEffect(() => {
    if (acceptedFiles && acceptedFiles?.length) {
      const currentFile = acceptedFiles[0];
      const name = currentFile?.name?.split('.');

      if (currentFile.size > 26214400) {
        setImageError('Max file size 25Mb.');
        return;
      }

      const uploadImage = async () => {
        setUploadingImage(true);
        const uploadedImage = await onUpload(acceptedFiles[0]);

        if (uploadedImage && uploadedImage.link) {
          setImageError('');
          dispatch(updateQuestion({ id, image: uploadedImage.link }));
          setUploadingImage(false);
        } else {
          setImageError('Image could not be uploaded');
        }
      };

      if (currentFile) {
        try {
          uploadImage();
        } catch (error) {
          setUploadingImage(false);
        }
      }
    }
  }, [acceptedFiles]);

  // Reset selected template when question type or variant changes
  useEffect(() => {
    const currentType = type?.value;
    const currentVariant = variant?.value;

    // Only reset if values actually changed
    if (prevTypeRef.current !== currentType || prevVariantRef.current !== currentVariant) {
      setSelectedTemplate(null);
    }

    // Update refs with current values
    prevTypeRef.current = currentType;
    prevVariantRef.current = currentVariant;
  }, [type?.value, variant?.value]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Paper
        className={`question__card ${isActive ? 'question__card--active ' : ''} ${
          (isGroupReference && !isActive) || !isEditable ? ' helper-question__preview' : ''
        } ${error && Object.keys(error)?.length && isActive ? 'question__card--error' : ''}`}
        onClick={handleOnClickQuestion}
      >
        <Box>
          {!isGroupReference && (
            <Typography className='text-bold text-uppercase'>Q{questionIndex}.</Typography>
          )}
          <Box className='d-flex sorting__container'>
            {isEditable && (
              <>
                <Tooltip title='Move up' arrow placement='top'>
                  <span>
                    <Button
                      className='sorting__button'
                      disabled={questionIndex === 1 || !isEditable}
                      onClick={(event) => handleOnSwitchQuestion(event, 'up')}
                    >
                      {<FontAwesomeIcon icon='sort-up' />}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title='Move down'
                  arrow
                  placement='bottom'
                  onClick={(event) => handleOnSwitchQuestion(event, 'down')}
                >
                  <span>
                    <Button className='sorting__button ' disabled={isLastQuestion || !isEditable}>
                      {<FontAwesomeIcon icon='sort-down' />}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ width: '100%' }}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px'
            }}
          >
            <Box
              md={9}
              style={{
                display: 'flex',
                alignItems: `${!isActive ? 'baseline' : 'end'}`,
                paddingRight: '16px',
                gap: '16px',
                width: '100%'
              }}
            >
              {isActive ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    width: '100%'
                  }}
                >
                  <InputLabel variant='standard' htmlFor='multiple-choice-options'>
                    Ask
                  </InputLabel>
                  <FormControl fullWidth>
                    <TextField
                      name='value'
                      value={title || ''}
                      error={error?.title && error?.title !== '' ? true : false}
                      helperText={error?.title}
                      onChange={handleOnchangeTitle}
                      InputProps={{ required: true }}
                      variant='standard'
                      className='custom-text-field'
                      style={{ marginRight: '20px', width: '100%' }}
                    />
                  </FormControl>
                </div>
              ) : (
                <>
                  <p className='question-title' style={{ marginTop: '4px' }}>
                    {title}
                  </p>
                </>
              )}
            </Box>

            <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {isActive && (
                <div className='question-types__wrapper'>
                  <Box className='question-types__container'>
                    <InputLabel id='assessment-select-question-type-label'>Type</InputLabel>
                    <Button
                      id={`question-header-dropdowm-button-${id}`}
                      aria-controls={isOpenPopover ? `question-header-dropdowm-button-${id}` : undefined}
                      aria-haspopup='true'
                      aria-expanded={isOpenPopover ? 'true' : undefined}
                      onClick={handleOnClickAnchor}
                      size='small'
                      className='question-types__button'
                    >
                      {getTypeLabel(type?.value)?.label}
                      {variant && variant?.label ? ' - ' + variant?.label : ''}
                      <FontAwesomeIcon icon='chevron-down' />
                    </Button>
                  </Box>

                  <Box sx={{ position: 'relative' }}>
                    <Popper
                      open={isOpenPopover}
                      anchorEl={anchorEl}
                      placement={placement}
                      transition
                      style={{ zIndex: 2 }}
                    >
                      {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={350}>
                          <Paper>
                            <ClickAwayListener onClickAway={handleOnClose}>
                              <div className='custom-popover__menu'>
                                {options &&
                                  options.map((option) => {
                                    const { _id, label, variants, divider } = option;

                                    return [
                                      <MenuItem
                                        className={
                                          divider
                                            ? divider === 'up'
                                              ? 'question-item__divider-up'
                                              : 'question-item__divider-down'
                                            : ''
                                        }
                                        key={_id}
                                        onClick={(event) => {
                                          handleClickQuestionType(event, option);
                                        }}
                                        value={type?.value}
                                        style={{ width: '100%', padding: '10px 16px' }}
                                      >
                                        <ListItemIcon style={{ minWidth: '22px', marginRight: '12px' }}>
                                          <FontAwesomeIcon
                                            icon={option?.icon}
                                            style={{ width: '22px', height: '22px' }}
                                          />
                                        </ListItemIcon>
                                        <ListItemText primary={label} />
                                        {variants?.length ? (
                                          <FontAwesomeIcon
                                            icon={selectedIndex === _id ? 'chevron-up' : 'chevron-down'}
                                          />
                                        ) : (
                                          <></>
                                        )}
                                      </MenuItem>,
                                      variants?.length ? (
                                        <Collapse
                                          key={`collapse-${_id}`}
                                          in={selectedIndex === _id}
                                          timeout='auto'
                                          unmountOnExit
                                        >
                                          {variants.map((variant, index) => (
                                            <List key={index} component='div' disablePadding>
                                              <MenuItem
                                                style={{ paddingLeft: '50px' }}
                                                onClick={(event) => {
                                                  handleClickQuestionType(event, option, variant);
                                                }}
                                              >
                                                <ListItemText primary={variant?.label || ''} />
                                              </MenuItem>
                                            </List>
                                          ))}
                                        </Collapse>
                                      ) : (
                                        <></>
                                      )
                                    ];
                                  })}
                              </div>
                            </ClickAwayListener>
                          </Paper>
                        </Fade>
                      )}
                    </Popper>
                  </Box>

                  {/* Layout Selector for Single Choice */}
                  {isSurvey && isSingleChoice && (
                    <Box className='question-types__container' sx={{ minWidth: 120 }}>
                      <InputLabel id='layout-select-label'>Layout</InputLabel>
                      <FormControl fullWidth size='small' variant='standard'>
                        <Select
                          labelId='layout-select-label'
                          id='layout-select'
                          value={config?.layout || 'vertical'}
                          onChange={handleOnChangeLayout}
                          sx={{
                            fontFamily: 'Poppins, Arial, sans-serif',
                            fontWeight: 500,
                            fontSize: '14px',
                            height: '34.59px',
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }
                          }}
                        >
                          <MenuItem value='vertical'>
                            <FontAwesomeIcon icon={['fal', 'arrow-down']} style={{ marginRight: '8px' }} />
                            Vertical
                          </MenuItem>
                          <MenuItem value='horizontal'>
                            <FontAwesomeIcon icon={['fal', 'arrow-right']} style={{ marginRight: '8px' }} />
                            Horizontal
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {isSurvey && (
                    <FormGroup>
                      <FormControlLabel
                        onClick={handleOnchangeRequired}
                        control={
                          <Checkbox
                            style={{ color: '#32668D' }}
                            checked={required}
                            disabled={isGroupReference}
                          />
                        }
                        label='Required'
                      />
                    </FormGroup>
                  )}
                </div>
              )}
              {showDelete && (
                <IconButton onClick={handleRemoveQuestion} style={{ height: '36.25px', width: '36.25px' }}>
                  <FontAwesomeIcon
                    icon='circle-minus'
                    style={{
                      color: '#0000008F',
                      width: '23.25px',
                      height: '23.25px'
                    }}
                  />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box xs={12}>
            {/* Template Selector for survey questions with options */}
            {isSurvey &&
              isActive &&
              !isGroupReference &&
              (type?.value === 'singlechoice' ||
                type?.value === 'multiplechoice' ||
                type?.value === 'ranking') && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <TemplateSelector
                    templateType='surveys'
                    questionType={type?.value}
                    questionVariant={variant?.value}
                    onSelectTemplate={handleTemplateSelection}
                    selectedTemplate={selectedTemplate}
                  />
                </Box>
              )}

            {questionBody}
            {isSurvey && isActive && !isGroupReference && (
              <Box
                xs={2}
                spacing={2}
                alignItems='center'
                direction='row'
                className='box-center'
                style={{
                  margin: '20px 0',
                  position: 'relative',
                  justifyContent: 'flex-start',
                  maxWidth: '200px'
                }}
              >
                <div
                  {...getRootProps({ multiple: false, className: 'dropzone' })}
                  className='box-center p-4'
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    border: '1px dashed currentColor',
                    borderRadius: '5px'
                  }}
                >
                  <input multiple={false} {...getInputProps({ multiple: false })} />
                  <div className='question__upload-tooltip'>
                    <IconTooltipButton
                      onClick={open}
                      icon='upload'
                      className='bg-third-blue text-white upload__button clickeable p-5 round-5-all'
                      title='upload'
                      disabled={uploadingImage}
                    />
                  </div>

                  {uploadingImage ? (
                    <div className='question__uploading'>
                      <div className='question__uploading__loader'>
                        <CircularProgress color='primary' size='30px' />
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                  {image && <img className='question__image p-2' src={image} alt={image} draggable='false' />}
                  {image && (
                    <div className='question__image__remove'>
                      <IconButton onClick={handleOnDeleteImage}>
                        <FontAwesomeIcon
                          icon='circle-minus'
                          style={{
                            color: '#f2f4f7',
                            width: '24px',
                            height: '24px'
                          }}
                        />
                      </IconButton>
                    </div>
                  )}

                  <p
                    className='text-muted-foreground opacity-75 py-2 px-2 bg-muted text-center text-sm m-2 '
                    onClick={open}
                    style={{ cursor: 'pointer' }}
                  >
                    Drop an image here, or click to select file
                  </p>
                  {imageError && <div className='text-error'>{imageError}</div>}
                </div>
              </Box>
            )}
          </Box>
        </Box>

        {isEditable && (
          <Tooltip title='Duplicate' arrow placement='bottom'>
            <span>
              <Button
                disabled={(title && title.length > 0) || (content && content.length > 0) ? false : true}
                className='question__card__clone-button '
                onClick={(event) => handleOnCloneQuestion(event, id)}
              >
                {<FontAwesomeIcon icon='clone' />}
              </Button>
            </span>
          </Tooltip>
        )}
      </Paper>
    </DragDropContext>
  );
};

export default Question;
